// =============================================================================
// MeuDriver — InstructorNotificationSettings (Batch T2)
// Configurações granulares de notificação do instrutor — /instrutor/notificacoes
// =============================================================================

import { Bell, BookOpen, MessageCircle, Star, Megaphone } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { useMyNotificationPrefs, useUpdateNotificationPrefs } from '@/hooks/useNotificationPrefsGranular'
import type { GranularNotificationPrefs } from '@/hooks/useNotificationPrefsGranular'

// ─── Toggle row ───────────────────────────────────────────────────────────────

function ToggleRow({
  label,
  description,
  checked,
  onToggle,
  disabled,
  testId,
}: {
  label: string
  description?: string
  checked: boolean
  onToggle: (v: boolean) => void
  disabled?: boolean
  testId?: string
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</p>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onToggle}
        disabled={disabled}
        data-testid={testId}
      />
    </div>
  )
}

function Group({
  label,
  icon,
  children,
}: {
  label: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="rounded-2xl border border-border/60 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-border/40 bg-slate-50 dark:bg-gray-800/50">
        {icon}
        <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100">{label}</h2>
      </div>
      <div className="px-5 divide-y divide-border/40">
        {children}
      </div>
    </div>
  )
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function InstructorNotificationSettings() {
  const { data: prefs, isLoading } = useMyNotificationPrefs()
  const { mutate: update, isPending } = useUpdateNotificationPrefs()

  function toggle(key: keyof GranularNotificationPrefs) {
    if (!prefs) return
    update({ [key]: !prefs[key] })
  }

  const disabled = isLoading || isPending

  return (
    <div className="space-y-6 max-w-xl">
      {/* Cabeçalho */}
      <div>
        <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white flex items-center gap-2">
          <Bell className="h-6 w-6 text-brand-600" />
          Notificações
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Controle quais eventos geram notificações in-app e por e-mail.
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 rounded-2xl" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Agendamentos */}
          <Group
            label="Agendamentos"
            icon={<BookOpen className="h-4 w-4 text-brand-600" />}
          >
            <ToggleRow
              label="Aula confirmada — notificação"
              description="Receba aviso no app quando um aluno confirmar uma aula"
              checked={prefs?.booking_confirmed_inapp ?? true}
              onToggle={() => toggle('booking_confirmed_inapp')}
              disabled={disabled}
              testId="toggle-booking-confirmed-inapp"
            />
            <ToggleRow
              label="Aula confirmada — e-mail"
              checked={prefs?.booking_confirmed_email ?? true}
              onToggle={() => toggle('booking_confirmed_email')}
              disabled={disabled}
              testId="toggle-booking-confirmed-email"
            />
            <ToggleRow
              label="Aula cancelada — notificação"
              checked={prefs?.booking_cancelled_inapp ?? true}
              onToggle={() => toggle('booking_cancelled_inapp')}
              disabled={disabled}
              testId="toggle-booking-cancelled-inapp"
            />
            <ToggleRow
              label="Aula cancelada — e-mail"
              checked={prefs?.booking_cancelled_email ?? true}
              onToggle={() => toggle('booking_cancelled_email')}
              disabled={disabled}
              testId="toggle-booking-cancelled-email"
            />
          </Group>

          {/* Mensagens */}
          <Group
            label="Mensagens"
            icon={<MessageCircle className="h-4 w-4 text-teal-600" />}
          >
            <ToggleRow
              label="Nova mensagem — notificação"
              checked={prefs?.new_message_inapp ?? true}
              onToggle={() => toggle('new_message_inapp')}
              disabled={disabled}
              testId="toggle-new-message-inapp"
            />
            <ToggleRow
              label="Nova mensagem — e-mail"
              checked={prefs?.new_message_email ?? true}
              onToggle={() => toggle('new_message_email')}
              disabled={disabled}
              testId="toggle-new-message-email"
            />
          </Group>

          {/* Avaliações */}
          <Group
            label="Avaliações"
            icon={<Star className="h-4 w-4 text-amber-500" />}
          >
            <ToggleRow
              label="Nova avaliação recebida — notificação"
              description="Receba aviso quando um aluno avaliar uma aula"
              checked={prefs?.review_received_inapp ?? true}
              onToggle={() => toggle('review_received_inapp')}
              disabled={disabled}
              testId="toggle-review-inapp"
            />
            <ToggleRow
              label="Nova avaliação recebida — e-mail"
              checked={prefs?.review_received_email ?? true}
              onToggle={() => toggle('review_received_email')}
              disabled={disabled}
              testId="toggle-review-email"
            />
          </Group>

          {/* Plataforma */}
          <Group
            label="Plataforma"
            icon={<Megaphone className="h-4 w-4 text-purple-600" />}
          >
            <ToggleRow
              label="Anúncios da plataforma"
              description="Comunicados e novidades do MeuDriver"
              checked={prefs?.announcement_inapp ?? true}
              onToggle={() => toggle('announcement_inapp')}
              disabled={disabled}
              testId="toggle-announcement-inapp"
            />
          </Group>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Notificações de pagamento e segurança não podem ser desativadas.
      </p>
    </div>
  )
}
